// Day.js
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
// Translation
import { useTranslation } from "react-i18next";
// React
import { useEffect } from "react";
// React Router DOM
import {
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
// Pages
import Home from "./pages/Home/Home";
import ImplementationGuidePage from "./pages/ImplementationGuide/ImplementationGuidePage";
import AcesoError from "./pages/Error/Error";
import AcesoInProgress from "./pages/InProgress/InProgress";
import PatientsViewer from "./pages/PatientsViewer/PatientsViewer"
import PatientsList from "./pages/PatientsList/PatientsList";
import CreateNote from "./pages/CreateNote/CreateNote";
import PatientCreation from "./pages/patientCreation/PatientCreation";
import NotesPage from "./pages/notesPage/NotesPage";
import CareteamPage from "./pages/createamPage/CareteamPage";
import StaysPage from "./pages/StaysPage/StaysPage";
import VisitsPage from "./pages/VisitsPage/VisitsPage";
import AppointmentsPage from "./pages/AppointmentsPage/AppointmentsPage";
import StaysDetailsPage from "./pages/ResourceDetailsPage/EpisodeOfCareDetailsPage/EpisodeOfCareDetailsPage";
import VisitsDetailsPage from "./pages/ResourceDetailsPage/EncounterDetailsPage/EncounterDetailsPage";
import AppointmentDetailsPage from "./pages/ResourceDetailsPage/AppointmentDetailsPage/AppointmentDetailsPage";
import i18n from "./i18n";

// Services
require("dayjs/locale/fr");

dayjs.extend(relativeTime);
dayjs.locale(i18n.language);

const applicationName = process.env.REACT_APP_NAME || "Aceso";

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const { i18n: translationInstance, t } = useTranslation();

  useEffect(() => {
    dayjs.locale(translationInstance.language);
  }, [translationInstance.language]);

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    const titleKey = Object.entries({
      "/PatientsList": "page.patientsList",
      "/PatientsViewer": "page.patientDetails",
      "/create-note": "page.createNote",
      "/PatientCreation": "page.patientCreation",
      "/NotesPage": "page.notes",
      "/CareTeamPage": "page.careCircle",
      "/InProgress": "page.inProgress",
      "/Error": "page.error",
      "/Pathway": "page.stays",
      "/Pathway/Stays": "page.stays",
      "/Pathway/Visits": "page.visits",
      "/Pathway/Appointments": "page.appointments",
      "/ImplementationGuide": "page.implementationGuide",
      "/Aceso/ImplementationGuide": "page.implementationGuide",
    })
      .sort(([firstPath], [secondPath]) => secondPath.length - firstPath.length)
      .find(([path]) => pathname === path || pathname.startsWith(`${path}/`))?.[1];
    const title = titleKey ? `${applicationName} - ${t(titleKey)}` : applicationName;
    const metaDescription = "";

    document.title = title;

    const metaDescriptionTag: HTMLMetaElement | null =
      document.querySelector('head > meta[name="description"]');

    if (metaDescriptionTag) {
      metaDescriptionTag.content = metaDescription;
    }
  }, [pathname, t]);

  return (
    <Routes>
      <Route index element={<Home />} />

      <Route path="/" element={<Home />} />
      <Route path="/Home" element={<Home />} />
      <Route path="/Aceso" element={<Home />} />
      <Route
        path="/InProgress"
        element={<AcesoInProgress />}
      />
      <Route
        path="/Error"
        element={<AcesoError />}
      />
      <Route
        path="/PatientsViewer/:patientId"
        element={<PatientsViewer />}
      />
      <Route
        path="/PatientsList"
        element={<PatientsList />}
      />
      <Route
        path="/create-note"
        element={<CreateNote />}
      />
      <Route
        path="/PatientCreation"
        element={<PatientCreation />}
      />
      <Route
        path="/NotesPage"
        element={<NotesPage />}
      />
      <Route
        path="/CareTeamPage"
        element={<CareteamPage />}
      />
      <Route
        path="/ImplementationGuide"
        element={<ImplementationGuidePage />}
      />
      <Route
        path="/Aceso/ImplementationGuide"
        element={<ImplementationGuidePage />}
      />
      <Route
        path="/Pathway"
        element={<StaysPage />}
      />
      <Route 
        path="/Pathway/Stays" 
        element={<StaysPage />} 
      />
      <Route 
        path="/Pathway/Visits" 
        element={<VisitsPage />} 
      />
      <Route 
        path="/Pathway/Appointments" 
        element={<AppointmentsPage />} 
      />
      <Route
        path="/Pathway/Stays/:resourceId"
        element={<StaysDetailsPage />}
      />
      <Route
        path="/Pathway/Visits/:resourceId"
        element={<VisitsDetailsPage />}
      />
      <Route
        path="/Pathway/Appointments/:resourceId"
        element={<AppointmentDetailsPage />}
      />
      <Route path="*" element={<AcesoError notFound />} />
    </Routes>
  );
}

export default App;
